import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1098 instructions for Tax Year 2025"
          description="Learn how to fill out IRS Form 1098 for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report mortgage interest and related payments."
          keywords=""
          canonical="resources/information-returns/form-1098-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Essential information required to file Form 1098
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 1098 (Mortgage Interest Statement)
                        </strong>{' '}
                        is used by lenders to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          mortgage interest of $600 or more
                        </strong>{' '}
                        received during the tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Borrower copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>,{' '}
                        the IRS filing deadline is{' '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        , for paper filings and{' '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>,{' '}
                        for electronic filings.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions, including
                        lender and borrower details,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>
                        (Mortgage Interest Received),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>
                        (Outstanding Mortgage Principal),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>
                        (Mortgage Origination Date),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4-6</strong>
                        (Refund of Overpaid Interest, Mortgage Insurance
                        Premiums, and Points Paid),{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Box 7-10
                        </strong>{' '}
                        (Property Information and Other Details), and{' '}
                        <strong style={{ fontWeight: 600 }}>Box 11</strong>
                        (Mortgage Acquisition Date).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late, incorrect, or non-electronic filing when required
                        may result in
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          IRS penalties ranging from $60 to $680 per return
                        </strong>
                        , depending on the filing delay and compliance
                        requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Form 1098, Mortgage Interest Statement, is a tax form used to
                  report mortgage interest received in the tax year by the
                  lender. The lender should{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1098/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1098
                  </Link>{' '}
                  to the IRS if the mortgage interest is more than $600. It is
                  the annual tax information return form.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who should file form 1098?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  A person or entity involved in a trade or business that
                  receives mortgage interest must file Form 1098 to report the
                  interest received. The IRS requires mandatory filing of Form
                  1098 for interest received in a tax year if it exceeds $600.
                  Voluntary filing of Form 1098 can be done even if the mortgage
                  interest is less than $600.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates in Form 1098
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  File Form 1098 before the deadline to avoid IRS penalties and
                  stay compliant. It is also important to send Borrower copy to
                  the borrower of the mortgage.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Filing With IRS
                </Typography>
                <Box component="ul" sx={{ mb: 2, ' & li': { mb: 1.5 } }}>
                  <li>
                    <b>E-Filing deadline: March 31, 2026</b>
                    <br />
                    Electronic filing of Form 1098 with the IRS should be done
                    before March 31, 2026, for the tax year 2025. You must file
                    electronically if you file 10 or more information in a tax
                    year{' '}
                  </li>
                  <li>
                    <b>Paper filing deadline: March 2, 2026</b> <br />
                    Paper filing of form 1098 with The IRS should be done before
                    March 2, 2026, for the tax year 2025.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Sending copies to Borrowers/Payers
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The lender should send a copy of Form 1098 to the Borrower
                  before February 2, 2026, for the tax year 2025. This applies
                  to both paper and E-filing of form 1098.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        File form 1098 with TaxZerone and send borrower/payer
                        copies <br />
                        using Zeronevault
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 110, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098instructions`}
                      >
                        File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Information needs to be reported in Form 1098
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1098 reports information including:
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Mortgage interest including points</li>
                  <li>Mortgage insurance premium</li>
                  <li>Reimbursements of overpaid interest</li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <b>Failure to E-file:</b> If there are more than 10
                    information returns that need to be filed for a tax year,
                    e-filing is mandatory. Failure will lead to $340 per return.
                  </li>
                  <li>
                    <b>Failure to file before the deadline:</b> The IRS has a
                    deadline to file Form 1098 and the late filing penalty can
                    go from  €¢ $60 per information return up to $4,098,500 per
                    year and the upper limit for small business penalties is
                    $1,366,000 per year.
                  </li>
                  <li>
                    <b>Failure to file correct information:</b> The information
                    in form 1098 is wrongly entered and can accumulate penalties
                    if it is not corrected before the deadline. The penalty is
                    the same as failure to file before the deadline.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Stack
                    direction={{ md: 'row', sm: 'row', xs: 'column' }}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '24px !important',
                        }}
                      >
                        Why Risk penalties?
                      </Typography>
                      <Typography variant="body2">
                        File now with TaxZerone and stay IRS-compliant
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        textAlign: { xs: 'center', sm: 'right' },
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098instructions`}
                      >
                        File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Guide to file form 1098
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Lender/Recipient details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/1098-lender-img.webp" />
                  <source srcSet="/assets/images/1098-lender-img.png" />
                  <img
                    src="/assets/images/1098-lender-img.png"
                    loading="lazy"
                    alt="Lender/Recipient details"
                    width={525}
                    height={310}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the lender/Recipient&apos;s name, address, telephone
                  number, and EIN if the lender is an entity or SSN if the filer
                  is an individual.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Borrower/Payer details and account number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-payer-details-img.webp" />
                  <source srcSet="/assets/images/1098-payer-details-img.png" />
                  <img
                    src="/assets/images/1098-payer-details-img.png"
                    loading="lazy"
                    alt="Borrower/Payer details and account number"
                    width={526}
                    height={335}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Borrower(Payer) tax identification number mostly
                  SSN, name, address
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-account-img.webp" />
                  <source srcSet="/assets/images/1098-account-img.png" />
                  <img
                    src="/assets/images/1098-account-img.png"
                    loading="lazy"
                    alt="Borrower/Payer details and account number"
                    width={525}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the account number if the lender has multiple mortgage
                  accounts with the borrower as the lender needs to file more
                  than one Form 1098{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Mortgage interest received form payer(s)/borrower(s)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-mortgage-img.webp" />
                  <source srcSet="/assets/images/1098-mortgage-img.png" />
                  <img
                    src="/assets/images/1098-mortgage-img.png"
                    loading="lazy"
                    alt="Box 1: Mortgage interest received form payer(s)/borrower(s)"
                    width={432}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the interest paid by the borrower on the mortgage during
                  the tax year excluding points. This payment includes interest
                  on a mortgage, a home equity loan, or a line of credit or
                  credit card loan secured by the property.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Outstanding Mortgage principal
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-outstanding-img.webp" />
                  <source srcSet="/assets/images/1098-outstanding-img.png" />
                  <img
                    src="/assets/images/1098-outstanding-img.png"
                    loading="lazy"
                    alt="Box 2: Outstanding Mortgage principal"
                    width={217}
                    height={67}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the remaining amount that needs to be paid as a mortgage
                  at the end of the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Mortgage Origination date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-origination-date-img.webp" />
                  <source srcSet="/assets/images/1098-origination-date-img.png" />
                  <img
                    src="/assets/images/1098-origination-date-img.png"
                    loading="lazy"
                    alt="Box 3: Mortgage Origination date"
                    width={217}
                    height={67}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the date when the mortgage document is signed between
                  the lender and the borrower.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Do not enter any value if the mortgage is
                  Acquired and enter the date of acquisition on Box 11.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Refund of Overpaid Interest
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-refund-img.webp" />
                  <source srcSet="/assets/images/1098-refund-img.png" />
                  <img
                    src="/assets/images/1098-refund-img.png"
                    loading="lazy"
                    alt="Box 4: Refund of Overpaid Interest"
                    width={217}
                    height={67}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the refund issued for the overpayment of the interest or
                  the credit for the overpayment of interest in the previous
                  year
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Mortgage Insurance Premiums
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-Insurance-img.webp" />
                  <source srcSet="/assets/images/1098-Insurance-img.png" />
                  <img
                    src="/assets/images/1098-Insurance-img.png"
                    loading="lazy"
                    alt="Box 5: Mortgage Insurance Premiums"
                    width={217}
                    height={67}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the insurance premium paid during the tax year including
                  prepaid premium.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Do not enter any value if the mortgage is
                  Acquired and enter the date of acquisition on Box 11.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Points Paid on Purchase of Principal Residence
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-paid-img.webp" />
                  <source srcSet="/assets/images/1098-paid-img.png" />
                  <img
                    src="/assets/images/1098-paid-img.png"
                    loading="lazy"
                    alt="Box 6: Points Paid on Purchase of Principal Residence"
                    width={432}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the points paid on the purchase of the borrower&apos;s
                  principal residence
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Address of Property Securing Mortgage
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-property-img.webp" />
                  <source srcSet="/assets/images/1098-property-img.png" />
                  <img
                    src="/assets/images/1098-property-img.png"
                    loading="lazy"
                    alt="Box 7: Address of Property Securing Mortgage"
                    width={432}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box, if the Borrower&apos;s current address and the
                  address of the property at which the mortgage is secured are
                  the same
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Address or description of property securing mortgage
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-address-property-img.webp" />
                  <source srcSet="/assets/images/1098-address-property-img.png" />
                  <img
                    src="/assets/images/1098-address-property-img.png"
                    loading="lazy"
                    alt="Box 8: Address or description of property securing mortgage"
                    width={432}
                    height={233}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you didn’t check Box 7, enter the address of the property
                  for which the mortgage is secured
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Number of Mortgaged Properties
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-number-property-img.webp" />
                  <source srcSet="/assets/images/1098-number-property-img.png" />
                  <img
                    src="/assets/images/1098-number-property-img.png"
                    loading="lazy"
                    alt="Box 9: Number of Mortgaged Properties"
                    width={264}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If there is more than one property bought using the mortgage
                  being reported, enter the number of properties.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Other
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-others-img.webp" />
                  <source srcSet="/assets/images/1098-others-img.png" />
                  <img
                    src="/assets/images/1098-others-img.png"
                    loading="lazy"
                    alt="Box 10: Other "
                    width={264}
                    height={84}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the details the filer wishes to convey to the borrower.
                  For Example, the collection agent can specify the name of the
                  person for whom you collected the interest and real estate
                  tax.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Mortgage Acquisition Date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1098-acquisition-date.webp" />
                  <source srcSet="/assets/images/1098-acquisition-date.png" />
                  <img
                    src="/assets/images/1098-acquisition-date.png"
                    loading="lazy"
                    alt="Box 11: Mortgage Acquisition Date"
                    width={171}
                    height={104}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the mortgage acquisition date if the mortgage is
                  acquired on the tax year else leave the box blank.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions for Form 1098
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              TaxZerone makes filing form 1098{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>
              Simple, Secure, and affordable
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File your Form 1098 form at just <b>$2.49</b>/each form
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: { lg: 18, md: 18, sm: 16, xs: 16 } }}
            >
              The more you file the more you save{' '}
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              The cost can go down up to <b>$0.59</b>/each form when you file in
              bulk
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098instructions`}
              >
                E-File Form 1098
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098Instructions;
